import { Method, isAxiosError } from 'axios';
import Cookie from 'js-cookie';
import config from '~/config';
import { getAxios } from './axios';

class HttpApiClient {
  constructor() {
    this.get = this.get.bind(this);
    this.post = this.post.bind(this);
    this.put = this.put.bind(this);
    this.patch = this.patch.bind(this);
    this.delete = this.delete.bind(this);
    this.invoke = this.invoke.bind(this);
  }

  async get(path: string, query?: MultiValueParams) {
    return this.invoke('GET', path, undefined, query);
  }

  async post(path: string, body?: any, query?: MultiValueParams) {
    return this.invoke('POST', path, body, query);
  }

  async put(path: string, body?: any, query?: MultiValueParams) {
    return this.invoke('PUT', path, body, query);
  }

  async patch(path: string, body?: any, query?: MultiValueParams) {
    return this.invoke('PATCH', path, body, query);
  }

  async delete(path: string, body?: any, query?: MultiValueParams) {
    return this.invoke('DELETE', path, body, query);
  }

  /**
   * We cannot use 404 status code in the API because we redirect
   * 404 requests to the base app to be able to host the react SPA in the edge.
   * So, we will use http 204 (No Content) for not found requests.
   */
  async invoke(method: Method, path: string, body?: any, query?: MultiValueParams) {
    if (path.includes('?')) throw new Error('Provide the query params using the query argument');

    const queryParams = Object.entries(query ?? {}).reduce((params, [key, vals]) => {
      if (typeof vals === 'string') {
        params.append(key, vals);
      } else if (Array.isArray(vals)) {
        for (const val of vals) {
          if (typeof val === 'string') {
            params.append(key, val);
          }
        }
      }
      return params;
    }, new URLSearchParams());

    const queryString = queryParams.toString();
    const fullPath = `${path}${queryString ? `?${queryString}` : ''}`;

    /**
     * Set the validation key in the headers if it is available in the window
     * The session is ephemeral, meaning the user need to login again if the
     * window is reload or closed
     */
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Csrf-Token': Cookie.get('xcsrftoken') ?? 'novalueset',
    };

    try {
      const axios = await getAxios();
      const { status, data } = await axios({
        method,
        baseURL: config.BFF_BASE_URL,
        url: fullPath,
        data: body ? JSON.stringify(body) : undefined,
        headers,
      });
      return { status, data };
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 401) window.location.reload();
      throw error;
    }
  }
}

export type MultiValueParams = Record<string, string | string[]>;

const client = new HttpApiClient();
export default client;
